import { useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { ChevronIcon } from 'src/components/ChevronIcon';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { StaticPanel } from 'src/components/StaticPanel';
import { ToneOfVoiceMenu } from 'src/components/ToneOfVoiceMenu';
import { ROTATE_DIRECTION } from 'src/data/ui';
import * as events from 'src/lib/events';
import { STONLY_ID } from 'src/lib/stonly';
import { useIsApplicationModeInfinity } from 'src/services';
import { usePrepareToneOfVoiceListProps } from 'src/services/usePrepareToneOfVoiceListProps';
import { useCurrentProject } from 'src/store';

import { ToneOfVoiceLabel } from './components/ToneOfVoiceLabel';
import { useFallbackToDefaultTov, useMaintainCorrectTovLanguage } from './services';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

export const BlockToneOfVoice = ({
  className,
  classes,
  title,
  generationTool,
  onSelect,
  disabled,
  stateVars,
  onChange,
  language,
}) => {
  const styles = useStyles({ classes });

  const [rootElement, setRootElement] = useState(null);

  const isInfinityMode = useIsApplicationModeInfinity();
  const currentProject = useCurrentProject();

  const {
    toneOfVoiceMenuOpened,
    toneOfVoices,
    customModels,
    selectedTov,
    selectedCustomModelId,
    draftToneOfVoice,
    toneOfVoiceError,
  } = Object.assign(useViewState(), stateVars);

  const { handleTovMenuOpen, handleTovMenuClose, handleSelectToneOfVoice } = useHandlers({
    onSelect,
    generationTool,
    onChange,
  });

  const toneOfVoiceListProps = usePrepareToneOfVoiceListProps({
    generationTool,
    language,
    eventsPage: events.EVENT_PAGE.editor,
    eventsSubPage: events.EVENT_SUB_PAGE.generationPanel,
    onSelect: handleSelectToneOfVoice,
  });

  useMaintainCorrectTovLanguage({
    customModels,
    toneOfVoices,
    selectedTov,
    selectedCustomModelId,
    language,
  });

  useFallbackToDefaultTov({
    selectedCustomModelId,
    projectType: currentProject?.type,
  });

  return (
    <StaticPanel
      className={cx(styles.root, className)}
      classes={{ title: styles.title }}
      title={title || 'Tone of voice'}
      titleStonlyId={STONLY_ID.toneOfVoice}
      ref={setRootElement}
    >
      <div
        className={cx(styles.tovPicker, {
          [styles.disabled]: disabled,
          [styles.active]: toneOfVoiceMenuOpened,
          [styles.error]: !!toneOfVoiceError,
        })}
        onClick={disabled ? null : handleTovMenuOpen}
      >
        <ToneOfVoiceLabel
          toneOfVoices={toneOfVoices}
          customModels={customModels}
          selectedTov={selectedTov}
          selectedCustomModelId={selectedCustomModelId}
          disabled={disabled}
          draftToneOfVoice={draftToneOfVoice}
          language={language}
        />

        <ChevronIcon
          className={styles.icon}
          direction={toneOfVoiceMenuOpened ? ROTATE_DIRECTION.right : ROTATE_DIRECTION.down}
        />
      </div>
      <ToneOfVoiceMenu
        referenceElement={rootElement}
        isMenuOpen={toneOfVoiceMenuOpened}
        onClose={handleTovMenuClose}
        isInfinityMode={isInfinityMode}
        ToneOfVoiceListProps={{
          ...toneOfVoiceListProps,
          generationTool,
          selectedTov,
          selectedCustomModelId,
          draftToneOfVoice,
          language,
          disabled,
        }}
      />
      {toneOfVoiceError && (
        <CustomTypography
          className={styles.error}
          variant={TYPOGRAPHY_VARIANT.regular12}
          color="error"
        >
          {toneOfVoiceError}
        </CustomTypography>
      )}
    </StaticPanel>
  );
};

BlockToneOfVoice.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  title: PropTypes.string,
  generationTool: PropTypes.string,
  stateVars: PropTypes.shape({}),
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  language: PropTypes.string,
};
BlockToneOfVoice.defaultProps = {
  className: null,
  classes: null,
  title: null,
  generationTool: null,
  stateVars: null,
  disabled: false,
  onSelect: null,
  onChange: null,
  language: null,
};
